import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WhatsAppTemplate, CreateTemplateData, UpdateTemplateData } from '@/types/whatsapp';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export function useWhatsAppTemplates(filters?: { category?: string; isActive?: boolean }) {
  return useQuery<WhatsAppTemplate[]>({
    queryKey: ['whatsapp', 'templates', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.isActive !== undefined)
        params.append('isActive', filters.isActive.toString());

      const res = await fetch(
        `${API_BASE_URL}/whatsapp/templates${params.toString() ? `?${params}` : ''}`,
        {
          headers: getAuthHeaders(),
        }
      );
      if (!res.ok) {
        throw new Error('Failed to fetch templates');
      }
      const data = await res.json();
      return data.data || data;
    },
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTemplateData) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/templates`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create template');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTemplateData }) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/templates/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update template');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'templates'] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/templates/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to delete template');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'templates'] });
    },
  });
}
